import { useEffect, useState } from 'react';
import type { ProgressStep } from '../api/chatClient';

/**
 * Live activity narration for an in-flight agent turn — the "what is it actually doing right now"
 * strip that replaces a bare typing indicator with the agent's own authored steps
 * ("Investigating your Teams problem" → "Searching Microsoft support articles" → "Preparing your answer").
 *
 * Every string rendered here arrives from the server, authored on the agent/skill/tool definition.
 * This component contributes NO wording of its own beyond the collapsed summary's counters, so it works
 * unchanged for any agent — including a localized one — and shows nothing at all for an agent that
 * narrates nothing.
 *
 * Two states:
 *  • `running` — the steps stack up, earlier ones settle back (dimmed, checked) and the newest leads.
 *  • `done`    — the stack collapses to a single expandable line kept beside the answer. The steps are
 *                deliberately NOT discarded on completion: "why did it say that" is asked after the
 *                answer arrives, not during, and a trace that vanishes at the moment of the answer is
 *                the one part of this the customer can never go back to.
 */
export function AgentActivitySteps({
  steps,
  state,
  elapsedMs,
}: {
  steps: ProgressStep[];
  state: 'running' | 'done';
  elapsedMs?: number;
}) {
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const styleId = 'cmsp-agent-progress-style';
    if (document.getElementById(styleId)) return;
    const style = document.createElement('style');
    style.id = styleId;
    // Motion is opt-out: the whole point of the strip is that it moves while the customer waits, but a
    // reader who asked the OS for less motion gets the same information without the entrance animation.
    style.textContent =
      '@keyframes cmsp-progress-in{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:none}}' +
      '@keyframes cmsp-progress-ring{0%,100%{opacity:.35;transform:scale(.8)}50%{opacity:1;transform:scale(1)}}' +
      '@media (prefers-reduced-motion: reduce){' +
      '.cmsp-progress-step{animation:none !important}' +
      '.cmsp-progress-marker-active{animation:none !important}}';
    document.head.appendChild(style);
  }, []);

  if (steps.length === 0) return null;

  const ordered = [...steps].sort((a, b) => a.sequence - b.sequence);

  if (state === 'done') {
    const seconds = elapsedMs != null && elapsedMs > 0 ? `${(elapsedMs / 1000).toFixed(1)}s` : null;
    return (
      <div style={styles.collapsedWrap}>
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          style={styles.collapsedButton}
          aria-expanded={expanded}
        >
          <Chevron open={expanded} />
          <span>
            {ordered.length} {ordered.length === 1 ? 'step' : 'steps'}
            {seconds ? ` · ${seconds}` : ''}
          </span>
        </button>
        {expanded && (
          <ol style={styles.collapsedList}>
            {ordered.map(step => (
              <li key={`${step.sequence}-${step.text}`} style={styles.collapsedItem}>
                {step.text}
              </li>
            ))}
          </ol>
        )}
      </div>
    );
  }

  return (
    <div style={styles.runningWrap} role="status" aria-live="polite">
      {ordered.map((step, index) => {
        // Only the newest step is "happening"; everything above it has already produced its result, so
        // it settles back visually instead of competing with the line the customer should be reading.
        const isCurrent = index === ordered.length - 1;
        return (
          <div
            key={`${step.sequence}-${step.text}`}
            className="cmsp-progress-step"
            style={{ ...styles.step, ...(isCurrent ? styles.stepCurrent : styles.stepDone) }}
          >
            <span style={styles.markerCell} aria-hidden="true">
              {isCurrent ? (
                <span className="cmsp-progress-marker-active" style={styles.markerActive} />
              ) : (
                <CheckMark />
              )}
            </span>
            <span>{step.text}</span>
          </div>
        );
      })}
    </div>
  );
}

function CheckMark() {
  return (
    <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path d="M2.5 6.4 4.7 8.6 9.5 3.8" stroke="#16a34a" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 12 12"
      fill="none"
      aria-hidden="true"
      style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s ease' }}
    >
      <path d="M4 2.5 8 6l-4 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const styles = {
  runningWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    minHeight: 20,
  } as React.CSSProperties,
  step: {
    display: 'grid',
    gridTemplateColumns: '14px 1fr',
    alignItems: 'center',
    gap: 8,
    fontSize: 13,
    lineHeight: 1.45,
    animation: 'cmsp-progress-in .22s ease-out both',
  } as React.CSSProperties,
  stepCurrent: {
    color: '#1f2937',
  } as React.CSSProperties,
  stepDone: {
    color: '#9ca3af',
  } as React.CSSProperties,
  markerCell: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 14,
    height: 14,
  } as React.CSSProperties,
  markerActive: {
    display: 'inline-block',
    width: 7,
    height: 7,
    borderRadius: 999,
    background: '#2563eb',
    animation: 'cmsp-progress-ring 1.2s ease-in-out infinite',
  } as React.CSSProperties,
  collapsedWrap: {
    marginTop: 6,
  } as React.CSSProperties,
  collapsedButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    padding: 0,
    border: 'none',
    background: 'transparent',
    color: '#6b7280',
    fontSize: 12,
    cursor: 'pointer',
    fontFamily: 'inherit',
  } as React.CSSProperties,
  collapsedList: {
    margin: '6px 0 0',
    padding: '0 0 0 18px',
    color: '#6b7280',
    fontSize: 12,
    lineHeight: 1.6,
  } as React.CSSProperties,
  collapsedItem: {
    listStyle: 'decimal',
  } as React.CSSProperties,
};
